import CollapsibleSection from "components/Section";
import React, { useCallback, useMemo, useRef } from "react";
import TextValue from "components/TitleValue";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { TWaterMeter, TWaterMeterInstallation } from "types/dto";

import { Grid } from "components/Grid";
import { getJalaliDate } from "utils/dateTimeTools";
import { useURLTools } from "utils/URLtools";

/** Installation records have no id, so synthesize a stable grid key. */
type TInstallationRow = TWaterMeterInstallation & {
  rowKey: string;
  isActive: boolean;
};

/** The backend marks a still-active installation with this sentinel. */
const isStillInstalled = (unInstallationDT?: string) =>
  !unInstallationDT || unInstallationDT.startsWith("0001-01-01");

type TProps = {
  data?: TWaterMeter;
  isLoading?: boolean;
};
export const MeterInstallationHistory = (props: TProps) => {
  const { data, isLoading } = props;

  const { t } = useTranslation();
  const { navigateWithQueryParams } = useURLTools();

  const gridRef = useRef(null);

  const currentInstallation = data?.currentInstallation;
  const apartmentId = currentInstallation?.apartmentId;

  const rows = useMemo<TInstallationRow[]>(() => {
    if (!data?.installationHistory) return [];
    return data.installationHistory.map((item, index) => ({
      ...item,
      // waterSubscriberId + installationDT identifies a record; index guards
      // against the backend repeating the same pair.
      rowKey: `${item.waterSubscriberId}-${item.installationDT}-${index}`,
      isActive: isStillInstalled(item.unInstallationDT),
    }));
  }, [data]);

  const renderInstallationHistory = useCallback(() => {
    if (rows.length === 0) {
      return (
        <p className={"text-sm text-gray-90"}>{t("noInstallationHistory")}</p>
      );
    }
    return (
      <>
        <Grid
          ref={gridRef}
          gridKey={"rowKey"}
          dataSource={rows}
          columns={[
            {
              dataField: "identifierId",
              caption: t("subscriberIdentifierId"),
            },
            {
              dataField: "floorNo",
              caption: t("floorNo"),
            },
            {
              dataField: "unitNo",
              caption: t("unitNo"),
            },
            {
              dataField: "address",
              caption: t("address"),
              visible: false,
            },
            {
              dataField: "installationDT",
              caption: t("installationDT"),
              dataType: "date",
              calculateCellValue: (rowData: any) => {
                return getJalaliDate({ date: rowData?.installationDT });
              },
            },
            {
              dataField: "unInstallationDT",
              caption: t("unInstallationDT"),
              dataType: "date",
              calculateCellValue: (rowData: any) => {
                // Renders "-" for the 0001-01-01 sentinel.
                return getJalaliDate({ date: rowData?.unInstallationDT });
              },
            },
            {
              dataField: "isActive",
              caption: t("status"),
              calculateCellValue: (rowData: any) => {
                return rowData?.isActive ? t("currentlyInstalled") : "";
              },
            },
          ]}
          hasSearchPanel={false}
          hasPagination={false}
          hasGroupingFeatures={false}
          hasFilterBuilder={false}
        />
      </>
    );
  }, [rows, t]);

  return (
    <CollapsibleSection
      title={t("installationInfo")}
      collapsible={true}
      isLoading={isLoading}
      skeleton="table"
    >
      {/* Two distinct blocks: the active installation, then the full history.
          Each sits in its own panel so they don't read as one long list. */}
      <div
        className={
          "w-full rounded-md border border-neutral-30 bg-neutral-00 p-3"
        }
      >
        <div
          className={"w-full flex items-center justify-between mb-3 gap-x-4"}
        >
          <span className="text-sm text-gray-90 font-bold">
            {t("currentInstallation")}:
          </span>
          {apartmentId && (
            <Button
              variant={"outlined"}
              size={"small"}
              icon={"MdApartment"}
              onClick={() =>
                navigateWithQueryParams(
                  `/dashboard/apartments/details/${apartmentId}`,
                  [],
                  { openInNewTab: true }
                )
              }
            >
              {t("goToApartmentDetails")}
            </Button>
          )}
        </div>
        <div className={"grid grid-cols-2 gap-y-3 gap-x-6 w-full"}>
          {currentInstallation ? (
            <>
              <TextValue
                title={t("subscriberIdentifierId")}
                value={currentInstallation.identifierId}
                className={"col-span-1"}
              />
              <TextValue
                title={t("installationDT")}
                value={getJalaliDate({
                  date: currentInstallation.installationDT,
                })}
                className={"col-span-1"}
              />
              <TextValue
                title={t("floorNo")}
                value={currentInstallation.floorNo}
                className={"col-span-1"}
              />
              <TextValue
                title={t("unitNo")}
                value={currentInstallation.unitNo}
                className={"col-span-1"}
              />
              <TextValue
                title={t("address")}
                value={currentInstallation.address}
                className={"col-span-2 justify-start gap-x-2"}
              />
            </>
          ) : (
            <p className={"text-sm text-gray-90 col-span-2"}>
              {t("meterNotInstalled")}
            </p>
          )}
        </div>
      </div>

      <div
        className={
          "w-full rounded-md border border-neutral-30 bg-white p-3 mt-4"
        }
      >
        <p className={"text-sm text-gray-90 font-bold mb-3"}>
          {t("installationHistory")}:
        </p>
        {renderInstallationHistory()}
      </div>
    </CollapsibleSection>
  );
};
